import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";

import { Checkbox, FormControlLabel, InputAdornment } from "@material-ui/core";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import decode from "jwt-decode";

import { bright_90, increaseBrightness } from "common/helpers/adjustColor";
import { getFooterLinks } from "common/helpers/getFooterLinks";
import ValidationHelper from "common/helpers/validation.helper";
import upsdkService from "common/services/upsdkService";
import configSelector from "common/store/selectors/configSelector";
import upsdkSelector from "common/store/selectors/upsdkSelector";

import CheckedCheckboxIcon from "../../../assets/icons/checked-checkbox.icon";
import PhoneIcon from "../../../assets/icons/phone.icon";
import EmailIcon from "../../../assets/icons/mail.icon";
import UncheckedCheckboxIcon from "../../../assets/icons/unchecked-checkbox.icon";
import AppButton from "../../../common/button/index.component";
import { Divider } from "@urbanpiper-engineering/meraki-components";
import InputField from "../../../common/fields/input-field/index.component";
import ServerError from "../../../common/server-error/index.component";
import Typography from "../../../common/typography/index.component";
import getCustomPageLinksForAuth from "../../../helpers/getCustomPageLinks";
import FacebookLoginIcon from "../../../assets/icons/facebook-login.icon";
import GoogleLoginIcon from "../../../assets/icons/google-login.icon";
import {
  COLOR_DARK_LIVER,
  COLOR_QUICK_SILVER,
} from "../../../constants/colors.constants";
import CountryHelper from "../../../helpers/countryCode.helper";
import { useTranslation } from "react-i18next";
import {
  FORMAT_TYPE_SVG,
  FORMAT_TYPE_UNICODE,
} from "../../../constants/enums.constants";
import MiscHelper from "common/helpers/misc.helper";
import useRouteHook from "../../../hooks/useRoute.hook";
import { BaseContext } from "../../../context/BaseContext";

import "./index.component.scss";

export default function SignIn({
  config,
  upsdk,
  primaryColor,
  primaryTextColor,
  guestCheckout,
  handleSetView,
  socialLoginSuccessCallback,
  setAuthResponse,
  userLoginFailureCallback,
  guestCheckoutCallback,
  setActiveAuthFlow,
  formData,
  setFormData,
  primaryLogin,
}) {
  const { isQrModeEnabled } = useContext(BaseContext);
  const [isTermAgree, setTermAgree] = useState(isQrModeEnabled);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(undefined);
  const { register, handleSubmit, errors } = useForm({
    mode: "onBlur",
    defaultValues: {
      phone: formData.phone,
      email: formData.email,
    },
  });
  const { t } = useTranslation();
  const { CustomLink } = useRouteHook();

  // variables
  const isd_code = upsdkSelector.getISDCode({ upsdk });
  const currency = upsdkSelector.getCurrency({ upsdk });
  const googleLoginConfig = configSelector.getGoogleLogin({ config });
  const facebookLoginConfig = configSelector.getFacebookLogin({ config });
  const footerLinks = getFooterLinks(configSelector.getCustomPages({ config }));
  const customPageLinks = getCustomPageLinksForAuth(footerLinks);

  useEffect(() => {
    /* global google */
    google.accounts.id.initialize({
      client_id: googleLoginConfig.clientID,
      callback: handleGoogleLoginSuccess,
    });

    google.accounts.id.renderButton(document.getElementById("signInDiv"), {
      theme: "outline",
      size: "large",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputChangeHandler = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const onSubmit = (data) => {
    setActiveAuthFlow("login");
    const { phone, email } = data;
    setLoading(true);

    let loginFunctionPromise;
    if (primaryLogin === "email") {
      loginFunctionPromise = upsdkService.loginWithEmail(email);
    } else {
      loginFunctionPromise = upsdkService.login(isd_code, phone);
    }

    loginFunctionPromise
      .then((response) => {
        handleSetView("otpVerify");
        setAuthResponse({
          isd_code: isd_code,
          customer_phone: phone,
          customer_email: email,
          ...response,
        });
        setLoginError(null);
      })
      .catch((error) => {
        const message = error.message.split(":")[0];
        if (message.trim() === "No profile found for user") {
          handleSetView("signUp");
          setAuthResponse({
            isd_code: isd_code,
            customer_phone: phone,
            customer_email: email,
          });
        } else {
          setLoginError(error.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGoogleLoginSuccess = (response) => {
    if (!response.credential) {
      handleGoogleLoginFailure(response);
      return;
    }
    const profileObj = decode(response.credential);
    setActiveAuthFlow("social");
    upsdkService
      .socialLogin({
        name: profileObj.name,
        email: profileObj.email,
        provider: "google",
        accessToken: response.credential,
      })
      .then((response) => {
        socialLoginSuccessCallback(response);
      })
      .catch((e) => {
        userLoginFailureCallback(e);
      })
      .finally(() => {});
  };

  const handleFacebookResponse = (response) => {
    setActiveAuthFlow("social");
    const { name, email, status, accessToken } = response;
    if (status === "unknown") {
      return;
    }
    upsdkService
      .socialLogin({
        name: name,
        email: email,
        provider: "facebook",
        accessToken: accessToken,
      })
      .then((response) => {
        socialLoginSuccessCallback(response);
      })
      .catch((e) => {
        userLoginFailureCallback(e);
      })
      .finally(() => {});
  };

  const handleGoogleLoginFailure = (response) => {
    console.log(response);
  };

  const handleChange = (event) => {
    setTermAgree(event.target.checked);
  };

  const handleGuestCheckoutClick = () => {
    guestCheckoutCallback("guestCheckout");
  };

  return (
    <div className="sign-in-form">
      <div className="dialog-content">
        <form onSubmit={handleSubmit(onSubmit)}>
          {primaryLogin === "phone" && (
            <InputField
              type="number"
              onChange={inputChangeHandler}
              refForm={register(ValidationHelper.phoneNumber(t, isd_code))}
              name="phone"
              errors={errors}
              label={
                <Typography
                  variant="h4"
                  weight="semiBold"
                  fontColor={primaryTextColor}
                  isRequired={true}
                >
                  {t("authDialog.phoneNumber")}
                </Typography>
              }
              inputIcon={<PhoneIcon className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(primaryColor, bright_90)}
              helperText={errors.phone && errors.phone.message}
              inputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {MiscHelper.isWindowsOS() ? (
                      <img
                        height="16px"
                        width="16px"
                        src={CountryHelper.getCountryFlag(
                          isd_code,
                          currency,
                          FORMAT_TYPE_SVG,
                        )}
                        alt="country-flag"
                      />
                    ) : (
                      CountryHelper.getCountryFlag(
                        isd_code,
                        currency,
                        FORMAT_TYPE_UNICODE,
                      )
                    )}
                    &nbsp;
                    {isd_code}
                  </InputAdornment>
                ),
              }}
            />
          )}
          {primaryLogin === "email" && (
            <InputField
              type="email"
              onChange={inputChangeHandler}
              refForm={register(ValidationHelper.email(t))}
              name="email"
              errors={errors}
              label={
                <Typography
                  variant="h4"
                  weight="semiBold"
                  fontColor={primaryTextColor}
                  isRequired={true}
                >
                  {t("authDialog.email")}
                </Typography>
              }
              inputIcon={<EmailIcon className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(primaryColor, bright_90)}
              helperText={errors.email && errors.email.message}
            />
          )}
        </form>

        {loginError && <ServerError message={loginError} />}

        <React.Fragment>
          <div className="terms-check-wrapper">
            <FormControlLabel
              classes={{ root: "terms-check" }}
              control={
                <Checkbox
                  icon={<UncheckedCheckboxIcon className="uncheck-icon" />}
                  checkedIcon={<CheckedCheckboxIcon className="check-icon" />}
                  onChange={handleChange}
                  checked={isTermAgree}
                  name="terms-check"
                />
              }
              label={
                <Typography
                  variant="h3"
                  weight="regular"
                  fontColor={COLOR_DARK_LIVER}
                  className="terms-check-label"
                >
                  {t("authDialog.iAgreeTo")}
                  <CustomLink
                    to={
                      customPageLinks.terms ||
                      "https://www.urbanpiper.com/legal/customer-terms-and-conditions"
                    }
                    target="_blank"
                  >
                    <Typography
                      variant="h3"
                      weight="regular"
                      fontColor={primaryColor}
                    >
                      {t("authDialog.terms")}
                    </Typography>
                  </CustomLink>{" "}
                  {t("authDialog.and")}
                  <CustomLink
                    to={
                      customPageLinks.policy ||
                      "https://www.urbanpiper.com/legal/privacy-policy"
                    }
                    target="_blank"
                  >
                    <Typography
                      variant="h3"
                      weight="regular"
                      fontColor={primaryColor}
                    >
                      {t("authDialog.privacy&Policy")}
                    </Typography>
                  </CustomLink>
                </Typography>
              }
            />
          </div>
        </React.Fragment>

        <AppButton
          fullWidth
          isDisabled={!isTermAgree}
          variant="contained"
          className="sign-in-button"
          isLoading={loading}
          buttonColor={primaryColor}
          onClickCallback={handleSubmit(onSubmit)}
        >
          <Typography
            variant="h2"
            weight="bold"
            className="sign-in-button-text"
          >
            {t("authDialog.continue")}
          </Typography>
        </AppButton>

        {guestCheckout && (
          <AppButton
            fullWidth
            variant="text"
            className="guest-checkout-button"
            isLoading={false}
            buttonColor={primaryColor}
            onClickCallback={handleGuestCheckoutClick}
          >
            <Typography
              variant="h3"
              weight="regular"
              className="guest-checkout-button-text"
              fontColor={primaryColor}
            >
              {t("authDialog.continueAsGuest")}
            </Typography>
          </AppButton>
        )}
      </div>

      <div className="dialog-footer">
        {(facebookLoginConfig.enable || googleLoginConfig.enable) && (
          <div className="social-login-divider">
            <Divider
              className="social-login-divider-line"
              variant="horizontal"
              color={COLOR_QUICK_SILVER}
              size="99%"
            />
            <Typography variant="h3" weight="regular">
              {t("common.or")}
            </Typography>
            <Divider
              className="social-login-divider-line"
              variant="horizontal"
              color={COLOR_QUICK_SILVER}
              size="99%"
            />
          </div>
        )}
        <div className="social-login-cta-wrapper">
          {facebookLoginConfig.enable && (
            <FacebookLogin
              isMobile={false}
              autoLoad={false}
              appId={facebookLoginConfig.appID}
              fields="name,email,picture"
              scope="public_profile,email"
              callback={handleFacebookResponse}
              render={(renderProps) => (
                <AppButton variant="text" onClickCallback={renderProps.onClick}>
                  <FacebookLoginIcon />
                </AppButton>
              )}
            />
          )}
          {googleLoginConfig.enable && (
            <div className="google-login-wrapper">
              <div id="signInDiv"></div>
              <AppButton variant="text">
                <GoogleLoginIcon />
              </AppButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
